import React, { useState, useCallback, useEffect } from 'react'
import { Modal, Typography, Button, Icon, TextField, ColorCircularProgress } from 'components'
import { makeStyles } from '@material-ui/core/styles'
import { colors } from 'constants/index'
import { useDispatch } from 'react-redux'
import { validateProviderEmail, shareCaseWithProviders, getCaseShareProviders } from '../../store/actions/cases'
import { ReactComponent as TrashIcon } from 'static/vault/menu/trash.svg'

const { WHITE } = colors

const useStyles = makeStyles({
  modal: {
    width: 752,
    position: 'relative',
    padding: '32px 40px 40px 40px',
    backgroundColor: WHITE,
    boxShadow: '0 2px 12px 0 rgba(87, 87, 87, 0.15)',
  },
  title: {
    margin: '0 0 12px 0',
    fontSize: 25,
    fontWeight: 700,
    lineHeight: '36px',
    color: '#2B4157',
    display: 'flex',
    alignItems: 'center',
  },
  subtitle: {
    marginBottom: 12,
    fontSize: 15,
    lineHeight: '19px',
    color: '#596C80',
  },
  emailRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    marginBottom: 5,
    position: 'relative',
  },
  emailInputContainer: {
    flex: 1,
    position: 'relative',
  },
  emailInputFirst: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#EBEFF5',
      borderColor: '#A5B3C2',
      '& fieldset': {
        borderColor: '#A5B3C2',
      },
      '&:hover fieldset': {
        borderColor: '#A5B3C2',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#A5B3C2',
      },
    },
  },
  emailInputError: {
    '& .MuiOutlinedInput-root': {
      borderColor: '#F71305',
      '& fieldset': {
        borderColor: '#F71305',
      },
    },
  },
  emailInput: {
    height: 40,
    '& .MuiOutlinedInput-input': {
      padding: '9px 11px',
      fontSize: 15,
      lineHeight: '19px',
      color: '#011A33',
    },
  },
  deleteIcon: {
    width: 16,
    height: 16,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '& svg': {
      width: '100%',
      height: '100%',
    },
  },
  errorHint: {
    marginTop: 2,
    fontSize: 15,
    lineHeight: '19px',
    color: '#F71305',
  },
  addButtonContainer: {
    marginTop: 12,
    marginBottom: 12,
  },
  addButton: {
    width: 32,
    height: 32,
    minWidth: 32,
    padding: 0,
    backgroundColor: '#FEF9F6',
    border: '1px solid #FAD2BB',
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      backgroundColor: '#FEF9F6',
      border: '1px solid #FAD2BB',
    },
    '& .MuiButton-startIcon': {
      margin: 0,
      color: '#EE5F10',
    },
  },
  description: {
    marginTop: 0,
    marginBottom: 0,
    fontSize: 15,
    lineHeight: '19px',
    color: '#596C80',
  },
  descriptionTitle: {
    marginBottom: 0,
    color: '#596C80',
  },
  descriptionList: {
    margin: 0,
    paddingLeft: 22.5,
    listStyle: 'disc',
  },
  descriptionItem: {
    marginBottom: 0,
    '&:last-child': {
      marginBottom: 0,
    },
  },
  boxBtn: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 14,
    marginTop: 16,
    '& button': {
      paddingLeft: 24,
      paddingRight: 24,
      minHeight: 40,
      paddingTop: 13,
      paddingBottom: 13,
      fontSize: 13,
      lineHeight: '14px',
      fontWeight: 500,
      letterSpacing: '0.6px',
      textTransform: 'uppercase',
    },
  },
  closeBtn: {
    backgroundColor: '#FEF9F6',
    border: '1px solid #F07735',
    color: '#F07735',
    '&:hover': {
      backgroundColor: '#FEF9F6',
      border: '1px solid #F07735',
    },
  },
  doneBtn: {
    backgroundColor: '#EE5F10',
    color: WHITE,
    minWidth: 127,
    '&:hover': {
      backgroundColor: '#EE5F10',
    },
    '&:disabled': {
      backgroundColor: '#EE5F10',
      opacity: 0.5,
    },
  },
})

const ShareWithProviderModal = ({
  isOpen,
  onClose,
  caseUuid,
  caseName,
  clientName,
  isAccountant,
  onDone,
  onRequestRemoveConfirmation,
}) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const [emails, setEmails] = useState([
    { id: 0, value: '', isValidating: false, isValid: null, error: '', isExisting: false },
  ])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isValidating, setIsValidating] = useState(false)
  const [isLoadingProviders, setIsLoadingProviders] = useState(false)
  const [existingProviders, setExistingProviders] = useState([])

  const validateEmail = useCallback(
    async (email, id) => {
      if (!email || !email.trim()) {
        return
      }

      setIsValidating(true)
      setEmails(prev => prev.map(item => (item.id === id ? { ...item, isValidating: true, error: '' } : item)))

      try {
        await dispatch(validateProviderEmail(caseUuid, email))
        setEmails(prev =>
          prev.map(item => (item.id === id ? { ...item, isValidating: false, isValid: true, error: '' } : item))
        )
      } catch (error) {
        const errorMessage =
          error.response?.data?.detail ||
          error.response?.data?.message ||
          "Provider with this email isn't registered on XIRA."
        setEmails(prev =>
          prev.map(item =>
            item.id === id ? { ...item, isValidating: false, isValid: false, error: errorMessage } : item
          )
        )
      } finally {
        setIsValidating(false)
      }
    },
    [caseUuid, dispatch]
  )

  const handleEmailChange = (id, value) => {
    setEmails(prev => prev.map(item => (item.id === id ? { ...item, value, isValid: null, error: '' } : item)))
  }

  const handleEmailBlur = (id, email) => {
    if (email && email.trim()) {
      validateEmail(email, id)
    }
  }

  const handleAddEmail = () => {
    const newId = Math.max(...emails.map(e => e.id), -1) + 1
    setEmails(prev => [
      ...prev,
      { id: newId, value: '', isValidating: false, isValid: null, error: '', isExisting: false },
    ])
  }

  const handleDeleteEmail = id => {
    setEmails(prev => prev.filter(item => item.id !== id))
  }

  const handleDone = async () => {
    setIsSubmitting(true)
    try {
      const validEmails = emails.map(e => e.value.trim()).filter(email => email && email.length > 0)

      // Re-validate all new emails before submitting
      const newEmails = emails.filter(e => e.value.trim() && !e.isExisting)
      let allValid = true

      if (newEmails.length > 0) {
        const validationPromises = newEmails.map(e =>
          dispatch(validateProviderEmail(caseUuid, e.value.trim())).catch(() => false)
        )

        const results = await Promise.all(validationPromises)
        allValid = results.every(result => result !== false)
      }

      if (allValid) {
        // Calculate providers to add and remove
        const originalEmails = existingProviders.map(p => p.username || '').filter(Boolean)
        const currentEmails = validEmails

        const providersToAdd = currentEmails.filter(email => !originalEmails.includes(email))
        const providersToRemove = originalEmails.filter(email => !currentEmails.includes(email))
        const totalProviderCount = validEmails.length

        // If there are providers to remove, close this modal and request confirmation from parent
        if (providersToRemove.length > 0 && onRequestRemoveConfirmation) {
          setIsSubmitting(false)
          handleClose()
          onRequestRemoveConfirmation(providersToAdd, providersToRemove, totalProviderCount, validEmails)
          return
        }

        // Share case with providers (can be empty arrays if removing all or adding new)
        await dispatch(
          shareCaseWithProviders(caseUuid, providersToAdd, providersToRemove, caseName, clientName, totalProviderCount)
        )

        if (onDone) {
          await onDone(validEmails)
        }
        handleClose()
      }
    } catch (error) {
      console.error('Error sharing with providers:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    setEmails([{ id: 0, value: '', isValidating: false, isValid: null, error: '', isExisting: false }])
    setIsSubmitting(false)
    setIsValidating(false)
    setIsLoadingProviders(false)
    setExistingProviders([])
    onClose()
  }

  // Fetch providers when modal opens
  useEffect(() => {
    if (isOpen && caseUuid) {
      setIsLoadingProviders(true)
      setExistingProviders([])
      dispatch(getCaseShareProviders(caseUuid))
        .then(providers => {
          // Filter out providers with is_master: true
          const filteredProviders = (providers || []).filter(provider => !provider.is_master)
          setExistingProviders(filteredProviders)
          setIsLoadingProviders(false)
        })
        .catch(error => {
          setExistingProviders([])
          setIsLoadingProviders(false)
        })
    }
  }, [isOpen, caseUuid, dispatch])

  // Initialize emails from existing providers when they're loaded
  useEffect(() => {
    if (!isLoadingProviders && isOpen) {
      if (existingProviders && existingProviders.length > 0) {
        const existingEmails = existingProviders.map((provider, index) => ({
          id: index,
          value: provider.username || '',
          isValidating: false,
          isValid: true,
          error: '',
          isExisting: true,
        }))
        // Add one empty row for new email
        existingEmails.push({
          id: existingProviders.length,
          value: '',
          isValidating: false,
          isValid: null,
          error: '',
          isExisting: false,
        })
        setEmails(existingEmails)
      } else {
        setEmails([{ id: 0, value: '', isValidating: false, isValid: null, error: '', isExisting: false }])
      }
    }
  }, [isOpen, existingProviders, isLoadingProviders])

  const hasValidationErrors = emails.some(e => e.value.trim() && !e.isExisting && (e.isValid === false || e.error))
  const isAnyValidating = emails.some(e => e.isValidating)
  // Allow submitting even if no emails (to remove all sharing)
  const isDoneDisabled = hasValidationErrors || isSubmitting || isValidating || isAnyValidating

  const getTitle = () => {
    const projectType = isAccountant ? 'file/project' : 'case/matter'
    return `Share '${caseName || ''}' ${projectType} of '${clientName || ''}' client`
  }

  return (
    <Modal open={isOpen} handleClose={handleClose} maxWidth={false}>
      <div className={classes.modal}>
        {isLoadingProviders ? (
          <ColorCircularProgress />
        ) : (
          <>
            <Typography variant="h3" className={classes.title}>
              {getTitle()}
            </Typography>
            <Typography variant="subtitle1" className={classes.subtitle}>
              Enter the email of a provider using XIRA to share the project with.
            </Typography>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              {emails.map((email, index) => (
                <div key={email.id}>
                  <div className={classes.emailRow}>
                    <div className={classes.emailInputContainer}>
                      <TextField
                        value={email.value}
                        variant="outlined"
                        fullWidth
                        type="email"
                        placeholder={'Enter email address'}
                        disabled={isSubmitting}
                        autoFocus={index === emails.length - 1 && emails.length > 1}
                        onChange={e => handleEmailChange(email.id, e.target.value)}
                        onBlur={() => handleEmailBlur(email.id, email.value)}
                        className={`${classes.emailInput} ${email.error ? classes.emailInputError : ''}`}
                        error={Boolean(email.error)}
                      />
                    </div>
                    {(emails.length > 1 || email.isExisting) && (
                      <div onClick={() => handleDeleteEmail(email.id)} className={classes.deleteIcon}>
                        <TrashIcon />
                      </div>
                    )}
                  </div>
                  {email.error && (
                    <Typography variant="body2" className={classes.errorHint}>
                      {email.error}
                    </Typography>
                  )}
                </div>
              ))}
            </div>
            <div className={classes.addButtonContainer}>
              <Button
                variant="text"
                onClick={handleAddEmail}
                startIcon={<Icon iconClass="las la-plus" />}
                className={classes.addButton}
                disabled={isSubmitting || isLoadingProviders}
              />
            </div>
            <div className={classes.description}>
              <Typography variant="subtitle1" className={classes.descriptionTitle}>
                By sharing a case/project, the invitee(s) will
              </Typography>
              <ul className={classes.descriptionList}>
                <li className={classes.descriptionItem}>Have access to the client information</li>
                <li className={classes.descriptionItem}>
                  Is allowed to create invoiceable activities for the case/project
                </li>
                <li className={classes.descriptionItem}>Has full access to the case/project document folder</li>
                <li className={classes.descriptionItem}>Can view all invoices created for the case/project</li>
                <li className={classes.descriptionItem}>
                  Payments received for case/project invoices will be placed in your wallet
                </li>
              </ul>
            </div>
            <div className={classes.boxBtn}>
              <Button
                variant="text"
                onClick={handleClose}
                className={classes.closeBtn}
                disabled={isSubmitting || isLoadingProviders}
              >
                cancel
              </Button>
              <Button variant="primary" onClick={handleDone} disabled={isSubmitting} className={classes.doneBtn}>
                done
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}

export default ShareWithProviderModal
